<template>
    <Dropdown
        ref="dropdown"
        :disabled="disabled"
        class="pause"
        :style="$attrs.style"
        :class="[
            $attrs.class,
            {
                'pause--paragraph-end': isParagraphEnd,
                'pause--paragraph-end--rtl': isParagraphEnd && bookRtl,
                'pause--active is-active': opened,
                'pause--bump': isBump,
                'is-playing': playing,
                'is-disabled': disabled,
            },
        ]"
        title="Pause editing"
        @opened="opened = true"
        @closed="opened = false"
    >
        <template #label>
            <button class="pause__trigger" v-bind="triggerAttrs">
                <div class="pause__icon" />
            </button>
        </template>
        <template v-if="opened" #items>
            <li ref="control" class="dropdown__item">
                <Spinner v-if="loading" />
                <div class="pause__container">
                    <!-- type selector -->
                    <div class="pause__inner">
                        <div class="pause__heading">Pause type</div>
                        <CustomSelect
                            v-model="type"
                            :options="pauseTypes"
                            :form-value-text="(item) => item.label"
                            compact
                        >
                            <template #default="{ item }">{{ item.label }}</template>
                        </CustomSelect>
                        <div v-if="type.slug !== 'audio'" class="pause__control">
                            <div class="pause__group">
                                <div class="pause__heading">Duration</div>
                                <NumberInput v-model="pause" step="0.05" :min="0.05" :max="8" />
                            </div>
                            <div class="pause__group">
                                <div class="pause__settings">
                                    <div class="pause__heading">{{ $t('pause.applyToAll') }}</div>
                                    <Checkbox
                                        v-model="applyTo"
                                        true-value="type_text"
                                        value="type_text"
                                        size="small"
                                        theme="inverted"
                                        :label="getApplyToOption(part.type_text.slug, type.slug)"
                                    />
                                    <Checkbox
                                        v-model="applyTo"
                                        true-value="type_part"
                                        value="type_part"
                                        size="small"
                                        theme="inverted"
                                        :label="getApplyToOption('', type.slug)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-else class="pause__control">
                            <BumpSelector v-model="bump" :bumps="bumps" :type="type" :part="part" />
                        </div>
                    </div>
                </div>
                <button
                    class="button pause__save-button"
                    :class="{ 'is-disabled': !changed }"
                    type="button"
                    :disabled="!changed"
                    @click.prevent="save"
                >
                    {{ $t('misc.save') }}
                </button>
            </li>
        </template>
    </Dropdown>
</template>

<script>
import Dropdown from '@speechki-frontend/shared/ui/Dropdown';
import { NumberInput, Checkbox, CustomSelect } from '@speechki-frontend/shared/ui/forms';

import BumpSelector from '../BumpSelector/BumpSelector';

import { mapState, mapActions, mapGetters } from 'vuex';

import { updateBookpart, swapBookpartType, setBookPauseDuration } from '../../api/bookpart';

import isEqual from 'lodash/isEqual';

const NAMES_MAP = {
    'book_name paragraph_end': 'Book title: pauses of book title',
    'book_name pause': 'Book title: pauses between sentences',
    'author paragraph_end': 'Authors: pauses of authors',
    'author pause': 'Authors: pauses between sentences',
    'title paragraph_end': 'Сhapter title: pauses of chapter title',
    'title pause': 'Сhapter title: pauses of chapter title',
    'default paragraph_end': 'Plain text: pauses between paragraph',
    'default pause': 'Plain text: pauses between sentences',
    'heading1 paragraph_end': 'Chapter subtitle: pauses of chapter subtitle',
    'heading1 pause': 'Chapter subtitle: pauses of chapter subtitle',
    'heading2 paragraph_end': 'Subtitle: pauses of subtitle',
    'heading2 pause': 'Subtitle: pauses of subtitle',
};

const getApplyToOption = (typeText = '', typePart) => {
    if (!typeText)
        return typePart === 'paragraph_end'
            ? 'All: pauses between paragraph'
            : 'All: pauses between sentences';

    return NAMES_MAP[`${typeText} ${typePart}`] || '';
};

export default {
    name: 'Pause',
    components: {
        Dropdown,
        NumberInput,
        BumpSelector,
        Checkbox,
        CustomSelect,
    },
    inheritAttrs: false,
    props: {
        part: {
            type: Object,
            required: true,
        },
        playing: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pause: undefined,
            initialPause: undefined,
            type: undefined,
            opened: false,
            applyTo: '',
            bump: null,
            loading: false,
        };
    },
    computed: {
        triggerAttrs() {
            // class/style stay on the root (Vue 2 merged them there)
            const { class: _class, style: _style, ...attrs } = this.$attrs;
            return attrs;
        },
        ...mapState({
            bumps: (state) => state.settings.bumps,
            book: (state) => state.book.book,
            pauseTypes: (state) => state.settings.swapTypes,
        }),
        ...mapGetters(['bookRtl']),

        changed() {
            if (this.initialPause !== this.pause) return true;

            if (this.applyTo.length) return true;

            if (!isEqual(this.type, this.part.type_part)) {
                if (this.type.slug === 'audio' && !this.bump) return false;

                return true;
            }

            if (this.isDifferentBump) {
                return true;
            }

            return false;
        },

        isParagraphEnd() {
            return this.part.type_part.slug === 'paragraph_end';
        },

        isBump() {
            return this.part.type_part.slug === 'audio';
        },

        isDifferentBump() {
            return (
                this.type.slug === 'audio' &&
                this.part.type_part.slug === 'audio' &&
                this.bump !== this.part.pre_recorded_audio.id
            );
        },
    },

    watch: {
        'part.pause': {
            handler(pause) {
                this.setValues(pause);
            },
        },
    },

    created() {
        this.setValues(this.part.pause);
        this.type = this.pauseTypes.find((type) => {
            return isEqual(type, this.part.type_part);
        });
        if (this.part.type_part.slug === 'audio') {
            this.bump = this.part.pre_recorded_audio.id;
        }
    },

    methods: {
        ...mapActions('pagedBookparts', ['fetchInitialBookparts']),
        getApplyToOption,

        setValues(pause) {
            this.pause = pause;
            this.initialPause = pause;
        },

        async save() {
            try {
                this.loading = true;
                if (this.type.slug !== this.part.type_part.slug || this.isDifferentBump) {
                    const data = {
                        type_part: this.type.slug,
                    };

                    if (this.type.slug === 'audio') {
                        data.pre_recorded_audio = this.bump;
                    }

                    await swapBookpartType(this.part.id, data);
                    await this.$store.dispatch('pagedBookparts/updateWithRefetch', {
                        id: this.part.id,
                        book: +this.$route.params.id,
                    });
                }

                if (this.pause !== this.initialPause) {
                    await updateBookpart(this.part.id, {
                        pause: this.pause,
                        pre_recorded_audio: '',
                    });
                    await this.$store.dispatch('pagedBookparts/updateWithRefetch', {
                        id: this.part.id,
                        book: +this.$route.params.id,
                    });
                }

                if (this.applyTo) {
                    const data = {
                        duration: this.pause,
                        type_part: this.type.slug,
                    };

                    if (this.applyTo === 'type_text') {
                        data.type_text = this.part.type_text.slug;
                    }

                    await setBookPauseDuration(this.book.id, data);
                    await this.fetchInitialBookparts({
                        page: this.$route.query.page,
                        preserveScroll: true,
                    });
                }

                this.applyTo = '';
            } catch (e) {
                console.log(e);
                this.$store.dispatch('pushToast', {
                    type: 'error',
                    title: `Error occured`,
                    text: 'Please try again later',
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" src="./Pause.scss"></style>
